import { useState } from "react";
import OrgStructure from "./settings/OrgStructure";
import UserPermissions from "./settings/UserPermissions";
import BasicInfo from "./settings/BasicInfo";
import MemberLevelConfig from "./settings/MemberLevelConfig";
import MemberMembershipYearConfig from "./settings/MemberMembershipYearConfig";
import ContributionConfig from "./settings/ContributionConfig";
import BenefitsConfig from "./settings/BenefitsConfig";
import RewardsConfig from "./settings/RewardsConfig";
import ActivityCategoryConfig from "./settings/ActivityCategoryConfig";
import PasswordSecurity from "./settings/PasswordSecurity";
import MemberNoRuleConfig from "./settings/MemberNoRuleConfig";
import PointsRulesConfig from "./settings/PointsRulesConfig";
import FormFieldSettings from "./settings/FormFieldSettings";

type SettingTab =
  | "basic"
  | "org"
  | "users"
  | "actcategory"
  | "levels"
  | "membership-years"
  | "benefits"
  | "contribution"
  | "points-rules"
  | "rewards"
  | "member-no"
  | "form-fields"
  | "password";

const tabs: { id: SettingTab; label: string; icon: string; desc: string }[] = [
  { id: "basic", label: "基本信息", icon: "ri-building-2-line", desc: "协会名称、简介、联系方式" },
  { id: "org", label: "组织架构", icon: "ri-organization-chart", desc: "配置协会职务与人员结构" },
  { id: "users", label: "用户权限", icon: "ri-shield-user-line", desc: "管理后台账号与操作权限" },
  { id: "actcategory", label: "活动分类", icon: "ri-calendar-2-line", desc: "活动类型字典，创建活动时选择" },
  { id: "levels", label: "会员等级", icon: "ri-medal-line", desc: "等级字典，配置专属权益" },
  { id: "benefits", label: "专属权益", icon: "ri-gift-2-line", desc: "权益条目字典，供等级勾选" },
  { id: "contribution", label: "积分科目字典", icon: "ri-award-line", desc: "积分科目名称、类型、单位与积分值" },
  { id: "points-rules", label: "积分规则", icon: "ri-coin-line", desc: "签到、活动、投票、邀请等积分获取规则" },
  { id: "rewards", label: "积分商城", icon: "ri-store-2-line", desc: "兑换商品管理与库存配置" },
  { id: "form-fields", label: "报名表单", icon: "ri-file-list-3-line", desc: "会员入会报名表字段设置" },
  { id: "member-no", label: "会员编号", icon: "ri-hashtag", desc: "审核通过时自动生成编号规则" },
  { id: "password", label: "密码安全", icon: "ri-lock-password-line", desc: "修改超级管理员或子账号登录密码" },
];

export default function SystemSettings() {
  const [activeTab, setActiveTab] = useState<SettingTab>("basic");

  const renderContent = () => {
    switch (activeTab) {
      case "basic": return <BasicInfo />;
      case "org": return <OrgStructure />;
      case "users": return <UserPermissions />;
      case "actcategory": return <ActivityCategoryConfig />;
      case "levels": return <MemberLevelConfig />;
      case "membership-years": return <MemberMembershipYearConfig />;
      case "benefits": return <BenefitsConfig />;
      case "contribution": return <ContributionConfig />;
      case "points-rules": return <PointsRulesConfig />;
      case "rewards": return <RewardsConfig />;
      case "form-fields": return <FormFieldSettings />;
      case "member-no": return <MemberNoRuleConfig />;
      case "password": return <PasswordSecurity />;
      default: return null;
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-lg font-bold text-gray-800">系统设置</h2>
        <p className="text-xs text-gray-400 mt-0.5">配置协会基础信息、权限和相关文件</p>
      </div>

      <div className="flex gap-2 mb-6 flex-wrap">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2.5 px-4 py-3 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap ${
              activeTab === tab.id ? "bg-red-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50 border border-gray-100"
            }`}
          >
            <i className={`${tab.icon} text-base`}></i>
            <div className="text-left">
              <p className="font-medium leading-tight">{tab.label}</p>
              <p className={`text-xs leading-tight mt-0.5 ${activeTab === tab.id ? "text-red-100" : "text-gray-400"}`}>{tab.desc}</p>
            </div>
          </button>
        ))}
      </div>

      <div>{renderContent()}</div>
    </div>
  );
}
